import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Facebook, 
  Instagram, 
  Twitter,
  Share2, 
  Check, 
  X, 
  ExternalLink,
  Settings,
  Heart,
  MessageCircle,
  Target,
  Award
} from 'lucide-react';

const SocialMediaIntegration = ({ currentUser }) => {
  const [socialStatus, setSocialStatus] = useState({
    facebook_connected: false,
    instagram_connected: false,
    twitter_connected: false,
    social_sharing_enabled: false,
    profile: {}
  });
  
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSocialStatus();
    fetchTemplates();
  }, []);

  const fetchSocialStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setSocialStatus(data);
    } catch (error) {
      console.error('Error fetching social status:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social/templates`);
      const data = await response.json();
      setTemplates(data.templates || {});
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const connectFacebook = async () => {
    try {
      setLoading(true);
      
      // Get Facebook auth URL
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social/facebook/auth-url`);
      const data = await response.json();
      
      // Open Facebook auth window
      const authWindow = window.open(data.auth_url, 'facebook-auth', 'width=600,height=600');
      
      // Listen for auth completion
      const authListener = (event) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'FACEBOOK_AUTH_SUCCESS') {
          authWindow.close();
          handleAuthCallback(event.data.code);
          window.removeEventListener('message', authListener);
        }
      };
      
      window.addEventListener('message', authListener);
      
    } catch (error) {
      console.error('Error connecting Facebook:', error);
      setMessage('Failed to connect Facebook account');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthCallback = async (code) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social/facebook/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          code: code,
          redirect_uri: `${window.location.origin}/auth/facebook/callback`
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessage('Facebook connected successfully!');
        fetchSocialStatus();
      } else {
        setMessage('Failed to connect Facebook account');
      }
    } catch (error) {
      console.error('Error handling auth callback:', error);
      setMessage('Failed to connect Facebook account');
    }
  };

  const shareToSocialMedia = async (platform, contentType, templateType) => {
    try {
      setSharing(true);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          platform: platform,
          content_type: contentType,
          template_type: templateType
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessage(`Successfully shared to ${platform}!`);
      } else {
        setMessage(data.error || 'Failed to share content');
      }
      
    } catch (error) {
      console.error('Error sharing content:', error);
      setMessage('Failed to share content');
    } finally {
      setSharing(false);
    }
  };

  const disconnectSocial = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social/disconnect`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessage('Social media accounts disconnected');
        fetchSocialStatus();
      }
    } catch (error) {
      console.error('Error disconnecting social media:', error);
    }
  };

  const getTemplateIcon = (templateType) => {
    const icons = {
      'first_session': <Target className="w-4 h-4" />,
      'great_match': <Heart className="w-4 h-4" />,
      'milestone': <Award className="w-4 h-4" />,
      'ai_features': <MessageCircle className="w-4 h-4" />
    };
    return icons[templateType] || <Share2 className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Status Message */}
      {message && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm">{message}</p>
          <button 
            onClick={() => setMessage('')}
            className="mt-2 text-blue-600 text-xs hover:text-blue-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Connection Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>Social Media Connections</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Facebook Connection */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center space-x-3">
                <Facebook className="w-6 h-6 text-blue-600" />
                <div>
                  <h3 className="font-medium">Facebook & Instagram</h3>
                  <p className="text-sm text-gray-600">
                    Share your dating success stories and milestones
                  </p>
                  {socialStatus.facebook_connected && socialStatus.profile?.name && (
                    <p className="text-xs text-green-600 mt-1">
                      Connected as {socialStatus.profile.name}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {socialStatus.facebook_connected ? (
                  <>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                      <Check className="w-3 h-3 mr-1" />
                      Connected
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={disconnectSocial}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={connectFacebook}
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Facebook className="w-4 h-4 mr-2" />
                    {loading ? 'Connecting...' : 'Connect'}
                  </Button>
                )}
              </div>
            </div>

            {/* Twitter Connection */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center space-x-3">
                <Twitter className="w-6 h-6 text-blue-400" />
                <div>
                  <h3 className="font-medium">Twitter</h3>
                  <p className="text-sm text-gray-600">
                    Share your dating journey and success stories
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Ready to share via Shot Clock Dating official account
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                  <Check className="w-3 h-3 mr-1" />
                  Ready
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sharing Templates */}
      {socialStatus.facebook_connected && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Share2 className="w-5 h-5" />
              <span>Share Your Dating Journey</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(templates).map(([key, template]) => (
                <div
                  key={key}
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-rose-100 rounded-lg">
                      {getTemplateIcon(key)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-sm">{template.title}</h3>
                      <p className="text-xs text-gray-600 mb-2">{template.description}</p>
                      <p className="text-xs text-gray-500 italic mb-3">
                        "{template.preview}..."
                      </p>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => shareToSocialMedia('facebook', 'post', key)}
                          disabled={sharing}
                          className="text-xs"
                        >
                          <Facebook className="w-3 h-3 mr-1" />
                          Facebook
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => shareToSocialMedia('instagram', 'story', key)}
                          disabled={sharing}
                          className="text-xs"
                        >
                          <Instagram className="w-3 h-3 mr-1" />
                          Story
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => shareToSocialMedia('twitter', 'post', key)}
                          disabled={sharing}
                          className="text-xs"
                        >
                          <Twitter className="w-3 h-3 mr-1" />
                          Twitter
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Privacy Notice */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="font-medium text-sm mb-2">Privacy & Control</h3>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• You control what gets shared and when</li>
          <li>• All sharing requires your explicit permission</li>
          <li>• You can disconnect social accounts anytime</li>
          <li>• We never share personal information about your matches</li>
        </ul>
      </div>
    </div>
  );
};

export default SocialMediaIntegration;